// Custom hook for secure authentication
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput } from '../utils/securityValidation';

// In-memory storage for sensitive data (more secure than localStorage)
let authToken = null;
let refreshToken = null;
let userId = null;

// Global function to get auth token for CSRF protection
export const getAuthToken = () => {
  return authToken;
};

const useSecureAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!authToken && !!userId;
  }, []);

  // Login function with security measures
  const login = useCallback(async (email, password) => {
    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);
    
    // Basic validation
    if (!sanitizedEmail || !sanitizedPassword) {
      throw new Error('Email and password are required');
    }
    
    try {
      // In a real implementation, this would be an API call
      // For demo purposes, we're simulating an API call
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: sanitizedEmail,
          password: sanitizedPassword
        })
      });
      
      if (!response.ok) {
        throw new Error('Authentication failed');
      }
      
      const data = await response.json();
      
      // Store tokens securely in memory
      authToken = data.authToken;
      refreshToken = data.refreshToken;
      userId = data.userId;
      
      setCurrentUser({
        id: data.userId,
        email: sanitizedEmail,
        name: data.name
      });
      
      return data;
    } catch (error) {
      // Clear tokens on failed login
      authToken = null;
      refreshToken = null;
      userId = null;
      throw error;
    }
  }, []);

  // Register function with security measures
  const register = useCallback(async (companyName, email, password) => {
    // Sanitize inputs
    const sanitizedCompanyName = sanitizeInput(companyName);
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);
    
    // Basic validation
    if (!sanitizedCompanyName || !sanitizedEmail || !sanitizedPassword) {
      throw new Error('All fields are required');
    }
    
    try {
      // In a real implementation, this would be an API call
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName: sanitizedCompanyName,
          email: sanitizedEmail,
          password: sanitizedPassword
        })
      });
      
      if (!response.ok) {
        throw new Error('Registration failed');
      }
      
      const data = await response.json();
      
      // Store tokens securely in memory
      authToken = data.authToken;
      refreshToken = data.refreshToken;
      userId = data.userId;
      
      setCurrentUser({
        id: data.userId,
        email: sanitizedEmail,
        name: data.name
      });
      
      return data;
    } catch (error) {
      // Clear tokens on failed registration
      authToken = null;
      refreshToken = null;
      userId = null;
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call logout API to invalidate tokens on server
      if (authToken) {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
      }
    } catch (error) {
      // Ignore errors during logout
      console.warn('Error during logout:', error);
    } finally {
      // Clear tokens
      authToken = null;
      refreshToken = null;
      userId = null;
      setCurrentUser(null);
      navigate('/login');
    }
  }, [navigate]);

  // Refresh token function
  const refreshTokenFunc = useCallback(async () => {
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    try {
      const response = await fetch('/api/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      
      const data = await response.json();
      
      // Update tokens
      authToken = data.authToken;
      refreshToken = data.refreshToken;
      
      return data;
    } catch (error) {
      // If refresh fails, logout user
      logout();
      throw error;
    }
  }, [logout]);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (authToken && userId) {
          // Validate token with server
          const response = await fetch('/api/validate-token', {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setCurrentUser({
              id: userId,
              email: data.email,
              name: data.name
            });
          } else {
            // Token invalid, logout
            logout();
          }
        }
      } catch (error) {
        // Ignore errors during validation
        console.warn('Error validating auth status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, [logout]);

  // Setup token refresh interval
  useEffect(() => {
    if (!authToken) return;
    
    const interval = setInterval(() => {
      // Refresh token 5 minutes before expiration
      refreshTokenFunc().catch(error => {
        console.warn('Token refresh failed:', error);
      });
    }, 10 * 60 * 1000); // Refresh every 10 minutes
    
    return () => clearInterval(interval);
  }, [authToken, refreshTokenFunc]);

  return {
    currentUser,
    login,
    register,
    logout,
    isAuthenticated,
    getAuthToken: () => authToken, // Return the current token
    loading
  };
};

export default useSecureAuth;